'use client';

import React, { useState } from 'react';
import { ChevronDown, Phone, MapPin, Clock, Volume2, VolumeX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

export interface RestaurantInfo {
  name: string;
  avatarUrl?: string;
  hours?: string;
  location?: string;
  phone?: string;
}

export interface DEAIChatHeaderProps {
  restaurantInfo?: RestaurantInfo;
  className?: string;
  isTTSMuted?: boolean;
  onTTSMuteToggle?: () => void;
}

export const DEAIChatHeader: React.FC<DEAIChatHeaderProps> = ({
  restaurantInfo = {
    name: 'Restaurant',
    hours: undefined,
    location: undefined,
    phone: undefined
  },
  className,
  isTTSMuted = false,
  onTTSMuteToggle
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={cn("shrink-0 bg-white/60 backdrop-blur-md border-b border-white/80 overflow-visible", className)}>
      {/* Header bar */}
      <div className="flex items-center gap-3 px-4 py-3 overflow-visible">
        {/* Avatar and Restaurant name - clickable area */}
        <div
          className="flex items-center gap-3 flex-1 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-300 to-blue-400 flex items-center justify-center text-white font-semibold text-lg shadow-lg overflow-hidden border-2 border-white/80">
            {restaurantInfo.avatarUrl ? (
              <img
                src={restaurantInfo.avatarUrl}
                alt={restaurantInfo.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span>{restaurantInfo.name.charAt(0).toUpperCase()}</span>
            )}
          </div>

          {/* Restaurant name */}
          <h2 className="flex-1 text-lg font-semibold text-gray-800">
            {restaurantInfo.name}
          </h2>

          {/* Chevron icon */}
          <ChevronDown
            className={cn(
              "w-5 h-5 text-gray-600 transition-transform duration-200",
              isExpanded && "rotate-180"
            )}
          />
        </div>

        {/* Language Switcher - separate clickable area */}
        <div className="shrink-0">
          <LanguageSwitcher />
        </div>

        {/* TTS Mute Button */}
        {onTTSMuteToggle && (
          <button
            onClick={onTTSMuteToggle}
            className={cn(
              "shrink-0 p-2 rounded-full transition-all duration-200",
              isTTSMuted
                ? "bg-red-100 text-red-600 hover:bg-red-200"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            )}
            title={isTTSMuted ? 'Unmute TTS' : 'Mute TTS'}
            aria-label={isTTSMuted ? 'Unmute text-to-speech' : 'Mute text-to-speech'}
          >
            {isTTSMuted ? (
              <VolumeX className="w-5 h-5" />
            ) : (
              <Volume2 className="w-5 h-5" />
            )}
          </button>
        )}
      </div>

      {/* Expanded info */}
      {isExpanded && (
        <div className="px-4 pb-3 space-y-2 animate-in slide-in-from-top-2 duration-200">
          {restaurantInfo.hours && (
            <div className="flex items-start gap-3 text-sm text-gray-700">
              <Clock className="w-4 h-4 mt-0.5 text-cyan-600 shrink-0" />
              <span>{restaurantInfo.hours}</span>
            </div>
          )}
          {restaurantInfo.location && (
            <div className="flex items-start gap-3 text-sm text-gray-700">
              <MapPin className="w-4 h-4 mt-0.5 text-cyan-600 shrink-0" />
              <span>{restaurantInfo.location}</span>
            </div>
          )}
          {restaurantInfo.phone && (
            <div className="flex items-start gap-3 text-sm text-gray-700">
              <Phone className="w-4 h-4 mt-0.5 text-cyan-600 shrink-0" />
              <a href={`tel:${restaurantInfo.phone}`} className="hover:underline">
                {restaurantInfo.phone}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
